import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { joinLatLong } from '@shared/utils/coords';
import { CargoPlaceQuantity } from '../../types/package-type';
import { CargoPlaceJsonForm } from '../../types/cargo-place';
import {
  CargoPlaceJson,
  ClientJson,
  FactPackageType,
  FactPackageTypeForm,
  OrderCargoPlaceDetailsFormWithFactPackageForm,
  PackageTypeJson,
  RateJson,
  SellerJson,
  SellerJsonForm,
} from '../../types/cargo';
import {
  AddressWithCoordinates,
  AddressWithCoordinatesForm,
  ClientForm,
  ClientWorkCondition,
  ClientWorkConditionForm,
} from '../../types/client';
import {
  Farm,
  FarmAddress,
  FarmAddressForm,
  FarmEditAddressForm,
  FarmForm,
} from '../../types/farm';
import {
  CalculatedRateJsonForm,
  DealDetailsJson,
  DealDetailsJsonForm,
} from '../../types/payment';
import {
  PreOrderCargoPlaceDetails,
  PreOrderCargoPlaceDetailsForm,
} from '../../types/pre-order';

@Injectable({
  providedIn: 'root',
})
export class FormsService {
  getOrderCargoPlaceDetailsForm = this.getPreOrderCargoPlaceDetailsForm;
  constructor(private readonly fb: FormBuilder) {}

  getSellerJsonForm(sellerJson?: SellerJson) {
    return this.fb.group<SellerJsonForm>({
      id: this.fb.control<Nullable<number>>(sellerJson?.id ?? null),
      inn: this.fb.control(sellerJson?.inn ?? null),
      name: this.fb.control(sellerJson?.name ?? null),
      nameFromInvoice: this.fb.control(sellerJson?.nameFromInvoice ?? null),
      uniqueKey: this.fb.control(sellerJson?.uniqueKey ?? null),
      publicAccess: this.fb.control(sellerJson?.publicAccess ?? null),
    });
  }

  getPackageTypeJsonForm(packageTypeJson?: PackageTypeJson) {
    return this.fb.group({
      tags: this.fb.control(packageTypeJson?.tags ?? []),
      id: this.fb.control<Nullable<number>>(packageTypeJson?.id ?? null),
      code: packageTypeJson?.code ?? null,
      name: packageTypeJson?.name ?? null,
      size: packageTypeJson?.size ?? null,
      cargoPlaceQuantityJson: this.fb.control<Nullable<CargoPlaceQuantity[]>>(
        packageTypeJson?.cargoPlaceQuantityJson ?? [],
      ),
      weight: this.fb.control<Nullable<number>>(
        packageTypeJson?.weight ?? null,
      ),
    });
  }

  getCargoPlaceJsonForm(
    cargoPlaceJson?: CargoPlaceJson,
  ): FormGroup<CargoPlaceJsonForm> {
    return this.fb.group<CargoPlaceJsonForm>({
      id: this.fb.control<Nullable<number>>(cargoPlaceJson?.id ?? null),
      code: this.fb.control(cargoPlaceJson?.code ?? ''),
      name: this.fb.control(cargoPlaceJson?.name ?? ''),
      size: this.fb.control(cargoPlaceJson?.size ?? ''),
      quantityInTrailerJson: this.fb.nonNullable.control(
        cargoPlaceJson?.quantityInTrailerJson ?? [],
      ),
    });
  }

  getFarmJsonForm(farm?: Farm): FormGroup<FarmForm> {
    return this.fb.group<FarmForm>({
      id: this.fb.control<Nullable<number>>(farm?.id ?? null),
      uniqueKey: this.fb.control(farm?.uniqueKey ?? null),
      flowerTypesJson: this.fb.nonNullable.control<string[]>(
        farm?.flowerTypesJson || [],
      ),
      name: this.fb.control(farm?.name ?? null),
      nameFromInvoice: this.fb.control(farm?.nameFromInvoice ?? null),
      imported: this.fb.control<Nullable<boolean>>(farm?.imported ?? null),
      precoolingRate: this.fb.control(farm?.precoolingRate ?? null),
      currencyLetter: this.fb.control(farm?.currencyLetter ?? null),
      publicAccess: this.fb.control(farm?.publicAccess ?? null),
      addressesJson: this.fb.nonNullable.control(farm?.addressesJson || []),
      taxNonPayer: this.fb.control(farm?.taxNonPayer ?? null),
      broker: this.fb.control(farm?.broker ?? null),
      comment: this.fb.control(farm?.comment ?? null),
      members: this.fb.nonNullable.control(farm?.members ?? []),
    });
  }

  getClientJsonForm(client?: ClientJson): FormGroup<ClientForm> {
    return this.fb.group<ClientForm>({
      id: this.fb.control<Nullable<number>>(client?.id ?? null),
      uniqueKey: this.fb.control(client?.uniqueKey ?? null),
      name: this.fb.control(client?.name ?? null),
      inn: this.fb.control(client?.inn ?? null),
      addressesWithCoordinatesJson: this.fb.nonNullable.control(
        client?.addressesWithCoordinatesJson ?? [],
      ),
      discount: this.fb.control(client?.discount ?? null),
      publicAccess: this.fb.control(client?.publicAccess ?? null),
      storageKeepersJson: this.fb.nonNullable.control(
        client?.storageKeepersJson ?? [],
      ),
      tgBotChatId: this.fb.control(client?.tgBotChatId ?? null),
      comment: this.fb.control(client?.comment ?? null),
      members: this.fb.nonNullable.control(client?.members ?? []),
      workCondition: this.getWorkConditionForm(
        client?.workCondition ?? undefined,
      ),
    });
  }

  getWorkConditionForm(workCondition?: ClientWorkCondition) {
    return this.fb.group<ClientWorkConditionForm>({
      credit: this.fb.control(workCondition?.credit ?? null),
      deliveryPaymentType: this.fb.control(
        workCondition?.deliveryPaymentType ?? null,
      ),
      documentPlace: this.fb.control(workCondition?.documentPlace ?? null),
      nomenclaturePlace: this.fb.control(
        workCondition?.nomenclaturePlace ?? null,
      ),
      providerSum: this.fb.control(workCondition?.providerSum ?? null),
      swiftSum: this.fb.control(workCondition?.swiftSum ?? null),
    });
  }

  getFactPackageTypeForm(factPackageType?: FactPackageType) {
    return this.fb.group<FactPackageTypeForm>({
      packageQuantity: this.fb.control<Nullable<number>>(
        factPackageType?.packageQuantity ?? null,
      ),
      packageQuantityLimit: this.fb.control<Nullable<number>>(
        factPackageType?.packageQuantityLimit ?? null,
      ),
      packageTypeJson: this.getPackageTypeJsonForm(
        factPackageType?.packageTypeJson,
      ),
    });
  }

  getRateJsonForm(rate?: RateJson) {
    return this.fb.group({
      id: this.fb.control<Nullable<number>>(rate?.id ?? null),
      currencyLetter: this.fb.control<Nullable<string>>(
        rate?.currencyLetter ?? null,
      ),
      measureUnitLetter: this.fb.control<Nullable<string>>(
        rate?.measureUnitLetter ?? null,
      ),
      cargoPlaceCode: this.fb.control<Nullable<string>>(
        rate?.cargoPlaceCode ?? null,
      ),
      rate: this.fb.control<Nullable<number>>(rate?.rate ?? null),
      dOn: this.fb.control<Nullable<string>>(rate?.dOn ?? null),
      dOff: this.fb.control<Nullable<string>>(rate?.dOff ?? null),
      updated: this.fb.control<Nullable<string>>(rate?.updated ?? null),
      imported: this.fb.control<Nullable<boolean>>(rate?.imported ?? null),
      flowerTypeId: this.fb.control<Nullable<string>>(
        rate?.flowerTypeId ?? null,
      ),
    });
  }

  getPreOrderCargoPlaceDetailsFormWithFactPackagesForm(
    cargoPlaceDetails?: PreOrderCargoPlaceDetails,
  ) {
    const factPackageForms: Array<FormGroup<FactPackageTypeForm>> =
      cargoPlaceDetails?.factPackageTypesJson.map(p => {
        return this.getFactPackageTypeForm(p);
      }) ?? [];

    return this.fb.group<OrderCargoPlaceDetailsFormWithFactPackageForm>({
      box: this.fb.control(cargoPlaceDetails?.box ?? null),
      cargoPlaceJson: this.getCargoPlaceJsonForm(
        cargoPlaceDetails?.cargoPlaceJson,
      ),
      cargoPlaceQuantity: this.fb.control(
        cargoPlaceDetails?.cargoPlaceQuantity ?? null,
      ),
      packageQuantity: this.fb.control(
        cargoPlaceDetails?.packageQuantity ?? null,
      ),
      packageQuantityLimit: this.fb.control(
        cargoPlaceDetails?.packageQuantityLimit ?? null,
      ),
      packageTypeJson: this.getPackageTypeJsonForm(
        cargoPlaceDetails?.packageTypeJson,
      ),
      factCargoPlaceQuantity: this.fb.control(
        cargoPlaceDetails?.factCargoPlaceQuantity ?? null,
      ),
      factPackageTypesJson:
        this.fb.array<FormGroup<FactPackageTypeForm>>(factPackageForms),
    });
  }

  getPreOrderCargoPlaceDetailsForm(
    cargoPlaceDetails?: PreOrderCargoPlaceDetails,
  ) {
    return this.fb.group<PreOrderCargoPlaceDetailsForm>({
      box: this.fb.control(cargoPlaceDetails?.box ?? null),
      cargoPlaceJson: this.getCargoPlaceJsonForm(
        cargoPlaceDetails?.cargoPlaceJson,
      ),
      cargoPlaceQuantity: this.fb.control(
        cargoPlaceDetails?.cargoPlaceQuantity ?? null,
      ),
      packageQuantity: this.fb.control(
        cargoPlaceDetails?.packageQuantity ?? null,
      ),
      packageQuantityLimit: this.fb.control(
        cargoPlaceDetails?.packageQuantityLimit ?? null,
      ),
      packageTypeJson: this.getPackageTypeJsonForm(
        cargoPlaceDetails?.packageTypeJson,
      ),
      factCargoPlaceQuantity: this.fb.control(
        cargoPlaceDetails?.factCargoPlaceQuantity ?? null,
      ),
      factPackageTypesJson: this.fb.nonNullable.control(
        cargoPlaceDetails?.factPackageTypesJson ?? [],
      ),
    });
  }

  getClientAddressWithCoordinatesJson(
    address?: AddressWithCoordinates,
  ): FormGroup<AddressWithCoordinatesForm> {
    return this.fb.group({
      address: this.fb.control(address?.address ?? null),
      longitude: this.fb.control(address?.longitude ?? null),
      latitude: this.fb.control(address?.latitude ?? null),
    });
  }

  getCalculatedJsonForm() {
    return this.fb.group<CalculatedRateJsonForm>({
      currencyLetter: this.fb.control(null),
      calculatedSum: this.fb.control(null),
      finalSum: this.fb.control(null),
    });
  }

  getDealDetailsJsonForm(dealDetails?: DealDetailsJson) {
    return this.fb.group<DealDetailsJsonForm>({
      paid: this.fb.control(dealDetails?.paid ?? null),
      paymentType: this.fb.control(dealDetails?.paymentType ?? null),
      paymentMethod: this.fb.control(dealDetails?.paymentMethod ?? null),
      recipientName: this.fb.control(dealDetails?.recipientName ?? null),
      recipientPhone: this.fb.control(dealDetails?.recipientPhone ?? null),
      comment: this.fb.control(dealDetails?.comment ?? null),
    });
  }

  getFarmEditAddressForm(farmAddress?: FarmAddress) {
    return this.fb.group<FarmEditAddressForm>({
      address: this.fb.control(farmAddress?.address ?? null),
      countryLetter: this.fb.control(farmAddress?.countryLetter ?? null),
      gateAccessCode: this.fb.control(farmAddress?.gateAccessCode ?? null),
      latLng: this.fb.control(
        farmAddress?.latitude && farmAddress?.longitude
          ? joinLatLong(farmAddress.latitude, farmAddress.longitude)
          : null,
      ),
    });
  }

  getFarmAddressForm(farmAddress?: FarmAddress) {
    return this.fb.group<FarmAddressForm>({
      address: this.fb.control(farmAddress?.address ?? null),
      countryLetter: this.fb.control(farmAddress?.countryLetter ?? null),
      gateAccessCode: this.fb.control(farmAddress?.gateAccessCode ?? null),
      longitude: this.fb.control(farmAddress?.longitude ?? null),
      latitude: this.fb.control(farmAddress?.latitude ?? null),
    });
  }
}
